import { httpClient } from '@app/features/analytics/services/http-client';

import {
  Event,
  SalesforceEcaCleanupStatus,
  SalesforceEcaMigration,
  SalesforceEcaMigrationStatus,
  TBankQuestion,
  TrayContext,
} from '../domain';
import {
  ApplicationSlugType,
  ApplicationType,
  OrganizationId,
} from '../types/types';
import { mainAPI, registrationsAPI, trayAPI } from './instance';
import { getTrayPayload } from './tray/payloads';
import { parseTrayResponseByKey } from './tray/utils';

type EndpointParamsType = {
  appSlug: ApplicationSlugType;
  orgId: string;
};

type Pagination = {
  page: number;
  totalPages: number;
  perPage: number;
  total: number;
};

export type EventsResponse = {
  data: {
    events: Event[];
    pagination: Pagination;
  };
};

export const getInstalledApplications = async (
  orgId: OrganizationId,
): Promise<ApplicationType[]> => {
  const { data } = await mainAPI.get(`/${orgId}/applications?installed=true`);

  return data.data;
};

export const fetchEvents = async (query: string): Promise<EventsResponse> => {
  const response = await httpClient.get(
    `/organizers/analytics/events?${query}`,
  );
  return response;
};

export const uninstallEventApplication = async ({
  appSlug,
  orgId,
}: EndpointParamsType) => {
  return mainAPI.post(`/${orgId}/applications/${appSlug}/uninstall`);
};

export const getTrayUrl = async ({
  appSlug,
  orgId,
}: EndpointParamsType): Promise<{
  data: { url: string };
}> => {
  const { data } = await mainAPI.get(
    `/${orgId}/applications/${appSlug}/tray_url`,
  );
  return data;
};

export const verifyTrayIntegration = async ({
  appSlug,
  orgId,
}: EndpointParamsType) => {
  return mainAPI.post(`/${orgId}/applications/${appSlug}/tray_verify`);
};

export const BANK_QUESTIONS_PER_PAGE = 1000;

/**
 * Organization Bank Questions from the internal registrations endpoint.
 *
 * Authenticated with the dashboard user token, so it needs neither an External
 * API token in the browser nor the :external_api payment plan feature. Returns
 * the same JSON:API resource shape as the External API equivalent.
 */
export const getOrganizationBankQuestions = async (
  orgId: OrganizationId,
): Promise<TBankQuestion[]> => {
  const { data } = await registrationsAPI.get(
    `/organizations/${orgId}/bank_questions?perPage=${BANK_QUESTIONS_PER_PAGE}`,
  );
  return data.data;
};

export const getSalesforceEcaMigration = async (
  orgId: OrganizationId,
): Promise<SalesforceEcaMigration> => {
  const { data } = await mainAPI.get(
    `/${orgId}/applications/salesforce/salesforce_reconnect`,
  );
  return data.data;
};

export const startSalesforceEcaMigration = async (
  orgId: OrganizationId,
  salesforceEnvironment?: 'production' | 'sandbox',
  installationConfirmed?: true,
): Promise<{
  status: SalesforceEcaMigrationStatus;
  attemptId: string;
  target: 'tray' | 'inhouse';
  url: string;
  popupType?: 'authentication' | 'configuration';
}> => {
  const { data } = await mainAPI.post(
    `/${orgId}/applications/salesforce/salesforce_reconnect/start`,
    salesforceEnvironment && installationConfirmed
      ? { salesforceEnvironment, installationConfirmed }
      : {},
  );
  return data.data;
};

export const completeSalesforceEcaMigration = async (
  orgId: OrganizationId,
  attemptId: string,
  authenticationId?: string,
): Promise<{ status: 'completed' | 'verifying' }> => {
  const { data } = await mainAPI.post(
    `/${orgId}/applications/salesforce/salesforce_reconnect/complete`,
    authenticationId ? { attemptId, authenticationId } : { attemptId },
  );
  return data.data;
};

export const resumeSalesforceEcaMigration = async (
  orgId: OrganizationId,
): Promise<{
  status: 'awaiting_authentication' | 'verifying' | 'completed';
  attemptId?: string;
  target: 'inhouse';
  url?: string;
  popupType?: 'authentication';
}> => {
  const { data } = await mainAPI.post(
    `/${orgId}/applications/salesforce/salesforce_reconnect/resume`,
  );
  return data.data;
};

export const cleanupSalesforceEcaMigration = async (
  orgId: OrganizationId,
): Promise<{
  status: 'completed';
  cleanupStatus: SalesforceEcaCleanupStatus;
}> => {
  const { data } = await mainAPI.post(
    `/${orgId}/applications/salesforce/tray_eca_migration/cleanup`,
  );
  return data.data;
};

type GetAppAuthUrlParams = EndpointParamsType & {
  name?: string;
  instance_type?: 'production' | 'sandbox';
};

export const getAppAuthUrl = async ({
  appSlug,
  orgId,
  name,
  instance_type,
}: GetAppAuthUrlParams): Promise<{
  data: { url: string; patUrl?: string; tokenUrl?: string };
}> => {
  const params: Record<string, string> = {};
  if (name) {
    params.name = name;
  }
  if (instance_type) {
    params.instance_type = instance_type;
  }

  const { data } = await mainAPI.get(
    `/${orgId}/applications/${appSlug}/tray_auth_url`,
    { params: Object.keys(params).length > 0 ? params : undefined },
  );
  return data;
};

export const uninstallDataApplication = async ({
  appSlug,
  orgId,
}: EndpointParamsType) => {
  return mainAPI.post(`/${orgId}/applications/${appSlug}/tray_disable`);
};

export const getTrayContext = async (
  appName: string,
  orgId: string,
): Promise<TrayContext> => {
  const response = await trayAPI.get(`/${orgId}/tray/${appName}/context`);
  return parseTrayResponseByKey('solutionInstances', response?.data);
};

/**
 * Makes a request to the Tray API with proper error handling and response parsing
 * @param appName The app name (e.g., 'marketo')
 * @param dataType The type of data to fetch
 * @param orgId The organization ID
 * @param variables Optional variables for the request
 * @returns The parsed response data
 */
export const getTrayData = async <T = any>(
  appName: string,
  dataType: string,
  orgId: string,
  variables?: Record<string, any>,
): Promise<T> => {
  try {
    const payload = getTrayPayload(dataType, variables);
    const response = await trayAPI.post(
      `/${orgId}/tray/${appName}/graphql`,
      JSON.parse(payload),
    );

    // Handle different response formats based on dataType
    if (dataType === 'callConnector') {
      const parsedOutput = JSON.parse(response.data.callConnector.output);
      const operation = variables?.operation;

      // Operations that return data directly (no .result wrapper)
      if (operation === 'list_lead_fields' || operation === 'list_lists') {
        return parsedOutput as unknown as T;
      }

      if (operation === 'raw_http_request') {
        const responseBody = parsedOutput.response?.body;
        return (appName === 'salesforce'
          ? responseBody
          : responseBody?.result ?? responseBody) as unknown as T;
      }

      // Default: try .result, fallback to parsed output
      return (parsedOutput.result ?? parsedOutput) as unknown as T;
    }

    if (dataType === 'authentications') {
      return parseTrayResponseByKey(dataType, response?.data) as unknown as T;
    }

    return response.data as T;
  } catch (error) {
    console.error(
      `Error fetching tray data for ${appName}/${dataType}:`,
      error,
    );
    throw error;
  }
};
